/**
 * The one session refresher for this page (see session-refresh.ts for why it exists). Every way a session ends,
 * signing out, locking or a 401 from the server, already announces SESSION_ENDED, so renewal stops there too.
 *
 * The refresh token is kept in this tab's sessionStorage beside the access token (api.ts), so a reload, a typed
 * URL or a bookmark resumes renewal. Kept only in memory (found live 2026-11-02), every reload silently dropped
 * the person five minutes later and threw them back to the home page. Same storage, same tab scope and same
 * lifetime rules as the access token; it is removed on every session end. Signatures still re-authenticate.
 */
import { getToken, SESSION_ENDED, setToken } from "./api.js";
import { refreshTokens } from "./auth.js";
import { createSessionRefresher, type TokenSet } from "./session-refresh.js";

export const REFRESH_KEY = "ebr.refresh";

const tabStorage = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.sessionStorage;

const inner = createSessionRefresher({
  exchange: refreshTokens,
  apply: setToken,
  setTimer: (fn, ms) => window.setTimeout(fn, ms),
  clearTimer: (h) => window.clearTimeout(h as number),
  onRenewed: (set) => remember(set.refreshToken),
});

function remember(refreshToken: string | null): void {
  const s = tabStorage();
  if (!s) return;
  if (refreshToken) s.setItem(REFRESH_KEY, refreshToken);
  else s.removeItem(REFRESH_KEY);
}

/** Seconds until a JWT's `exp`, or 0 when it cannot be read (renew at once, never assume it is fresh). */
export function secondsLeft(jwt: string, nowMs: number): number {
  try {
    const payload = JSON.parse(atob((jwt.split(".")[1] ?? "").replace(/-/g, "+").replace(/_/g, "/"))) as {
      exp?: unknown;
    };
    return typeof payload.exp === "number" ? Math.max(0, payload.exp - nowMs / 1000) : 0;
  } catch {
    return 0;
  }
}

export const sessionRefresher = {
  start(set: TokenSet): void {
    remember(set.refreshToken);
    inner.start(set);
  },
  stop(): void {
    remember(null);
    inner.stop();
  },
  /** On page load: carry on renewing a session this tab already holds. */
  resume(nowMs = Date.now()): boolean {
    const access = getToken();
    const refreshToken = tabStorage()?.getItem(REFRESH_KEY) ?? null;
    if (!access || !refreshToken) return false;
    inner.start({ accessToken: access, refreshToken, expiresIn: secondsLeft(access, nowMs) });
    return true;
  },
};

if (typeof window !== "undefined") {
  window.addEventListener(SESSION_ENDED, () => sessionRefresher.stop());
  sessionRefresher.resume();
}
